import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import useOffersUI from 'api/useOffersUI';
import usePermissions from 'api/usePermissions';
import useTheme from 'api/useTheme';
import { ETaskId, OfferUI } from 'common/contracts';
import { useFormik } from 'formik';
import { useUnsavedChanges } from 'hooks/useUnsavedChanges';
import { useChecklistState } from 'state/hooks/setup-checklist.hook';
import { EToasterStatus, showToast } from 'utils/showToast';
import { DEFAULT_BORDER_COLOR, DEFAULT_BORDER_WIDTH, PROGRESS_BAR_DEFAULTS, PROMOTION_DEFAULTS, SUBTITLE_SUPPORTED_TEMPLATES } from 'views/OfferDesign/OfferDesign.consts';
import { OfferDesignTypes, OfferTypeFormConfig } from 'views/OfferDesign/OfferDesign.types';
import { formatName, sanitizeExternalId } from 'views/OfferDesign/OfferDesign.utils';
import { OfferFormLayout } from 'views/Offers/components/OfferFormLayout';

import Button from 'components/Button/Button';

import { BundleFormFields } from './components/BundleFormFields';
import { OfferDesignPreview } from './components/OfferDesignPreview';
import { ProgressBarFormFields } from './components/ProgressBarFormFields';
import { PromotionFormFields } from './components/PromotionFormFields';
import { buildBundlePayload, buildProgressBarPayload, buildPromotionPayload } from './OfferDesignForm.utils';
import { bundleSchema, progressBarSchema, promotionSchema } from './OfferDesignForm.validation';

const OFFER_TYPE_CONFIG: Partial<Record<OfferDesignTypes, OfferTypeFormConfig>> = {
  [OfferDesignTypes.Bundle]: {
    FormFields: BundleFormFields,
    schema: bundleSchema,
    buildPayload: buildBundlePayload
  },
  [OfferDesignTypes.SpecialOffer]: {
    FormFields: PromotionFormFields,
    schema: promotionSchema,
    buildPayload: buildPromotionPayload
  },
  [OfferDesignTypes.ProgressBar]: {
    FormFields: ProgressBarFormFields,
    schema: progressBarSchema,
    buildPayload: buildProgressBarPayload
  }
};

export const OfferDesignForm = () => {
  const { publisherId, offerUIId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const isDup = location.pathname.includes('/dup/');
  const isEdit = !!offerUIId && !isDup;
  const offerType = (new URLSearchParams(location.search).get('type') as OfferDesignTypes) || OfferDesignTypes.Bundle;
  const config = OFFER_TYPE_CONFIG[offerType];

  const { canUserEdit } = usePermissions();
  const { setTaskStatus, hasCategories, isWebStore } = useChecklistState();
  const { addOfferUI, updateOfferUI, getOfferUI, getOffersUI } = useOffersUI(publisherId, offerUIId);
  const { getGeneralTheme: generalTheme } = useTheme(publisherId);

  const [isNameUnique, setIsNameUnique] = useState(true);
  const [isExternalIdUnique, setIsExternalIdUnique] = useState(true);

  const shouldDisplaySubTitle = useMemo(
    () => !!generalTheme?.data?.specialOffersInternalViewModel &&
      SUBTITLE_SUPPORTED_TEMPLATES.includes(generalTheme.data.specialOffersInternalViewModel),
    [generalTheme.data?.specialOffersInternalViewModel]
  );

  const initialValues = useMemo(() => {
    const data = getOfferUI?.data;
    const bgColor = offerType === OfferDesignTypes.Bundle
      ? data?.backgroundColor
      : data?.specialOffer?.backgroundColor;

    const promotionFields = offerType === OfferDesignTypes.SpecialOffer ? {
      collectText: data?.specialOffer?.collectText || PROMOTION_DEFAULTS.collectText,
      title: data?.specialOffer?.title || '',
      fontSize: data?.specialOffer?.fontSize || PROMOTION_DEFAULTS.fontSize,
      fontWeight: data?.specialOffer?.fontWeight || PROMOTION_DEFAULTS.fontWeight,
      fontColor: data?.specialOffer?.fontColor || PROMOTION_DEFAULTS.fontColor,
      subTitleText: data?.specialOffer?.subTitle?.text || '',
      subTitleFontSize: data?.specialOffer?.subTitle?.fontSize || PROMOTION_DEFAULTS.subFontSize,
      subTitleFontWeight: data?.specialOffer?.subTitle?.fontWeight || PROMOTION_DEFAULTS.fontWeight,
      subTitleFontColor: data?.specialOffer?.subTitle?.fontColor || PROMOTION_DEFAULTS.fontColor
    } : {};

    const progressBarFields = offerType === OfferDesignTypes.ProgressBar ? {
      backgroundOpacity: data?.progressBar?.backgroundOpacity ?? PROGRESS_BAR_DEFAULTS.backgroundOpacity,
      frameRadius: data?.progressBar?.frameRadius ?? PROGRESS_BAR_DEFAULTS.frameRadius,
      barFillColor: data?.progressBar?.barFillColor || PROGRESS_BAR_DEFAULTS.barFillColor,
      barBackgroundColor: data?.progressBar?.backgroundColor || PROGRESS_BAR_DEFAULTS.backgroundColor,
      barRadius: data?.progressBar?.barRadius ?? PROGRESS_BAR_DEFAULTS.barRadius,
      borderColor: data?.borderColor || PROGRESS_BAR_DEFAULTS.borderColor,
      borderWidth: data?.borderWidth ?? PROGRESS_BAR_DEFAULTS.borderWidth,
      accumulatedItemImageUrl: data?.progressBar?.accumulatedItem?.imageUrl || '',
      textColor: data?.progressBar?.textColor || PROGRESS_BAR_DEFAULTS.textColor
    } : {};

    return {
      name: formatName(data?.name, isDup),
      externalId: sanitizeExternalId(data?.externalId),
      backgroundImage: data?.backgroundImage || '',
      backgroundColor: bgColor || '',
      buttonSuffixImage: data?.buttonSuffixImage || '',
      buttonSuffixAnimation: data?.buttonSuffixAnimation || '',
      borderColor: data?.borderColor || DEFAULT_BORDER_COLOR,
      borderWidth: data?.borderWidth ?? DEFAULT_BORDER_WIDTH,
      offerUiType: offerType,
      ...promotionFields,
      ...progressBarFields
    };
  }, [getOfferUI?.data, isDup, offerType]);

  const formTitle = useMemo(() => {
    if (isEdit) return 'Edit offer design';
    if (isDup) return 'Duplicate offer design';
    return 'Create new offer design';
  }, [isEdit, isDup]);

  const handleClose = useCallback(() => navigate('../'), [navigate]);

  const handleSubmitSuccess = useCallback(() => {
    showToast({
      message: `Offer Design ${isEdit ? 'saved' : 'added'} successfully`,
      status: EToasterStatus.SUCCESS
    });
    if (!isEdit && hasCategories && isWebStore) {
      setTaskStatus(ETaskId.CREATE_OFFER_DESIGN);
    }
    (isEdit ? getOfferUI : getOffersUI).refetch();
    navigate('../');
  }, [isEdit, hasCategories, isWebStore, setTaskStatus, getOfferUI, getOffersUI, navigate]);

  const handleSubmitError = useCallback(() => {
    showToast({
      message: `Error ${isEdit ? 'editing' : 'adding'} Offer Design`,
      status: EToasterStatus.ERROR
    });
  }, [isEdit]);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    dirty,
    setFieldValue
  } = useFormik({
    validateOnMount: true,
    enableReinitialize: true,
    initialValues,
    validationSchema: config?.schema,
    onSubmit: async (formValues) => {
      if (!config) return;
      const payload = config.buildPayload(formValues, offerType);
      const callbacks = { onSuccess: handleSubmitSuccess, onError: handleSubmitError };

      if (isEdit && offerUIId) {
        updateOfferUI.mutate({ offerId: offerUIId, form: payload }, callbacks);
      } else {
        addOfferUI.mutate(payload, callbacks);
      }
    }
  });

  const handleSave = useCallback(() => submitForm(), [submitForm]);

  const isFormValid = useMemo(
    () => isValid && (dirty || !isEdit) && canUserEdit,
    [isValid, dirty, isEdit, canUserEdit]
  );

  const formikProps = {
    values,
    errors: errors as Record<string, string | undefined>,
    touched: touched as Record<string, boolean | undefined>,
    handleChange,
    handleBlur,
    setFieldValue
  };

  useEffect(() => {
    if (offerUIId && publisherId) {
      getOfferUI.refetch();
    }
  }, [offerUIId, publisherId]);

  useEffect(() => {
    if (!isEdit && getOffersUI.data?.result) {
      const { result } = getOffersUI.data;
      setIsNameUnique(!result.some((o: OfferUI) => o.name === values.name));
      setIsExternalIdUnique(
        !result.some((o: OfferUI) => o.externalId === values.externalId)
      );
    }
  }, [getOffersUI.data, isEdit, values.name, values.externalId]);

  useUnsavedChanges({ dirty });

  if (!config) return null;

  const { FormFields } = config;

  return (
    <OfferFormLayout
      title={formTitle}
      isFormValid={isFormValid}
      onSave={handleSave}
      onClose={handleClose}
      previewContent={<OfferDesignPreview offerType={offerType} values={values} />}
      showPreviewToggle={false}
    >
      <div className="w-[600px] pt-10 pb-10 h-max">
        <Button
          variant="link"
          onClick={handleClose}
          className="p-0 h-auto font-semibold mb-5 gap-[9px] text-indigo-600 no-underline hover:no-underline hover:text-indigo-600"
        >
          <FontAwesomeIcon icon="fa-regular fa-arrow-left" />
          Back
        </Button>
        <FormFields
          {...formikProps}
          isNameUnique={isNameUnique}
          isExternalIdUnique={isExternalIdUnique}
          shouldDisplaySubTitle={shouldDisplaySubTitle}
        />
      </div>
    </OfferFormLayout>
  );
};
